import { Command } from "commander";

import { scriptJsonPath, type CommandOutput } from "../common.js";
import { withScriptFileLock } from "../infra/script-working-copy.js";
import { emit } from "../output.js";

export type Handler = (
  opts: Record<string, unknown>,
) => Promise<[CommandOutput, number]> | [CommandOutput, number];

export interface RunState {
  jsonMode: boolean;
  exitCode: number;
  format?: string;
}

const scriptAccess = new WeakMap<Command, "read" | "write">();

function camelToSnake(value: string): string {
  return value.replace(/([A-Z])/g, "_$1").toLowerCase();
}

function normalizeOpts(opts: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(opts).map(([key, value]) => [camelToSnake(key), value]));
}

function mergedCommandOpts(command: Command): Record<string, unknown> {
  const chain: Command[] = [];
  let current: Command | null | undefined = command;
  while (current) {
    chain.unshift(current);
    current = current.parent;
  }
  return Object.assign({}, ...chain.map((item) => item.opts()));
}

export function dispatch(handler: Handler, state: RunState) {
  return async (...args: unknown[]) => {
    const command = args[args.length - 1] as Command;
    const positionals = args.slice(0, -2).filter((value) => value !== undefined) as string[];
    const normalized = normalizeOpts(mergedCommandOpts(command));
    if (positionals.length > 0) normalized["_args"] = positionals;
    state.jsonMode = Boolean(normalized["json"]);
    state.format = typeof normalized["format"] === "string" ? normalized["format"] : undefined;

    const invoke = async () => handler(normalized);
    const [output, exitCode] = scriptAccess.get(command) === "write"
      ? await withScriptFileLock(scriptJsonPath(normalized), invoke)
      : await invoke();
    emit(output, exitCode, state.jsonMode, state.format);
    state.exitCode = exitCode;
  };
}

export function describeArguments(command: Command, descriptions: readonly string[]): Command {
  command.registeredArguments.forEach((argument, index) => {
    argument.description = descriptions[index] ?? "";
  });
  return command;
}

export function withScriptOpts(command: Command, access: "read" | "write" = "write"): Command {
  scriptAccess.set(command, access);
  return command.option("-p, --script-path <path>", "Script JSON file (default: ./workspace/script.json)");
}

export function withWorkingCopyOpts(command: Command): Command {
  scriptAccess.set(command, "write");
  return command
    .option("-p, --script-path <path>", "Local script JSON file (default: ./workspace/script.json)")
    .option("--project-group-no <no>", "Remote project group number");
}

export function withSelection(command: Command): Command {
  return command
    .option("-i, --in <selector>", "Address or address range to select")
    .option("-t, --type <type>", "Action type to match")
    .option("-a, --actor <id>", "Actor ID to match")
    .option("-g, --grep <pattern>", "Text or regular expression to match")
    .option("--has <facet>", "Require a populated action facet")
    .option("--apply", "Apply the planned changes")
    .option("--plan <digest>", "Exact plan digest to apply")
    .option("-n, --dry-run", "Preview changes without writing");
}

export const appendString = (value: string, previous: string[]): string[] => [...previous, value];
